import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { fmtNumber } from "@/lib/format";
import { FileUpload, HistoryCard } from "@/components/reconcile/ReconcileParts";
import { Loader2, Scale, Search } from "lucide-react";

const STORE = "moringa.reconcile.text";
const STATUS = {
    mismatch: { label: "Different", cls: "bg-loss/15 text-loss border-loss/30" },
    not_in_app: { label: "Missing in app", cls: "bg-loss/15 text-loss border-loss/30" },
    not_in_broker: { label: "Not at broker", cls: "bg-accent/15 text-accent border-accent/30" },
    match: { label: "Match", cls: "bg-gain/15 text-gain border-gain/30" },
};

function Summary({ s }) {
    return (
        <div className="flex gap-2 flex-wrap" data-testid="reconcile-summary">
            {Object.entries(STATUS).map(([k, v]) => (
                <Badge key={k} variant="outline" className={`${v.cls} num`} data-testid={`reconcile-count-${k}`}>{v.label}: {s[k] || 0}</Badge>
            ))}
        </div>
    );
}

function Row({ r }) {
    const st = STATUS[r.status];
    return (
        <tr className="border-t border-border/50" data-testid={`reconcile-row-${r.ticker}`}>
            <td className="py-2 pr-3 font-semibold num">{r.ticker}{r.warning && <span className="ml-1.5 text-[10px] text-loss" title={r.warning}>old ticker</span>}</td>
            <td className="pr-3 text-right num">{r.broker == null ? "—" : fmtNumber(r.broker, 4)}</td>
            <td className="pr-3 text-right num">{fmtNumber(r.app, 4)}</td>
            <td className={`pr-3 text-right num ${r.diff ? "text-loss" : ""}`}>{r.diff == null ? "—" : `${r.diff > 0 ? "+" : ""}${fmtNumber(r.diff, 4)}`}</td>
            <td className="pr-3"><Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge></td>
            <td className="pr-3 text-xs text-muted-foreground">{Object.entries(r.subs).map(([s, v]) => `${s}: ${fmtNumber(v, 4)}`).join(" · ")}</td>
            <td className="text-right">
                {r.status !== "match" && (
                    <Link to={`/issues?check=${encodeURIComponent(r.ticker)}`} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid={`reconcile-check-${r.ticker}`}>
                        <Search className="w-3 h-3" />Position check
                    </Link>
                )}
            </td>
        </tr>
    );
}

export default function Reconcile() {
    const qc = useQueryClient();
    const [text, setText] = useState(() => localStorage.getItem(STORE) || "");
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showMatches, setShowMatches] = useState(false);
    const [source, setSource] = useState("pasted");

    const run = async () => {
        setBusy(true);
        localStorage.setItem(STORE, text);
        try {
            const { data } = await api.post("/reconcile", { text, source });
            setRes(data);
            qc.invalidateQueries({ queryKey: ["reconcile-history"] });
        }
        catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const onFile = (data, name) => { setText(data.text); setSource(name); setRes(null); };
    const rows = (res?.rows || []).filter((r) => showMatches || r.status !== "match");

    return (
        <div className="space-y-5" data-testid="reconcile-page">
            <div>
                <p className="text-sm text-muted-foreground">Paste your broker's current positions, one per line as <span className="num">TICKER, shares</span> (a CSV export works too). They're compared with your holdings across all sub-portfolios. Nothing is changed.</p>
            </div>
            <Card className="p-4 space-y-3">
                <Textarea value={text} onChange={(e) => { setText(e.target.value); setSource("pasted"); }} rows={8} placeholder={"AAPL, 140\nMSFT, 25.5\nTTE\t300"}
                    className="num text-xs" data-testid="reconcile-input" />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        <FileUpload onLoaded={onFile} />
                        <span className="text-xs text-muted-foreground">Duplicate tickers (several accounts) are added up. Header and total lines are ignored.</span>
                    </div>
                    <Button onClick={run} disabled={busy || !text.trim()} data-testid="reconcile-run-btn">
                        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Scale className="w-4 h-4 mr-1.5" />}Compare
                    </Button>
                </div>
            </Card>
            {res && (
                <Card className="p-4 space-y-3" data-testid="reconcile-results">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Summary s={res.summary} />
                        <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={showMatches} onChange={(e) => setShowMatches(e.target.checked)} data-testid="reconcile-show-matches" />Show matches
                        </label>
                    </div>
                    {res.skipped_lines.length > 0 && <p className="text-xs text-muted-foreground" data-testid="reconcile-skipped">Lines not read: {res.skipped_lines.join(", ")}</p>}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
                                <tr><th className="text-left py-1">Ticker</th><th className="text-right pr-3">Broker</th><th className="text-right pr-3">App</th><th className="text-right pr-3">App − broker</th><th className="text-left">Status</th><th className="text-left">By sub-portfolio</th><th></th></tr>
                            </thead>
                            <tbody>{rows.map((r) => <Row key={r.ticker} r={r} />)}</tbody>
                        </table>
                        {!rows.length && <p className="text-sm text-muted-foreground py-4" data-testid="reconcile-all-match">Everything matches your broker.</p>}
                    </div>
                </Card>
            )}
            <HistoryCard />
        </div>
    );
}
